import Axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import IAuthentication from './models/IAuthentication';
export default class Api {
    private endpoint: string = '/api';
    baseUrl: string;
    formName: string = "";

    constructor(controllername : string, formName ?: string) {
        this.baseUrl = `${this.endpoint}/${controllername}`;
        if (formName) {
            this.formName = formName;
        }
        let userFromStorage : IAuthentication = JSON.parse(sessionStorage.getItem('user') || '{}');
        if (userFromStorage.token) {
            Axios.interceptors.request.use(
                (config) => {
                    // Set default request headers for every request
                    config.headers.contentType = "application/json";
                    config.headers.authorization = `Bearer ${userFromStorage.token}`;
                    config.headers.post['userId'] = userFromStorage.id;
                    return config;
                },
                (error) => {
                    // Do something with request error
                    // eslint-disable-next-line no-console
                    console.log('Error:' + error);
                    return Promise.reject(error.message);
                }
            );
        }
    }

    async get<T>(subpath : string = "", params ?: { [key: string] : any}, config ?: AxiosRequestConfig) : Promise<T | void> {
        const url = this.constructUrl(subpath, params);
        return Axios.get<T>(url.href, config)
        .then(response => response.data)
        .catch((err : AxiosError) => {
            console.error(`Request to ${subpath} failed: ${err.message}`);
        });
    }

    async getRaw(subpath : string = "", params ?: { [key: string] : any}, config ?: AxiosRequestConfig) : Promise<AxiosResponse> {
        const url = this.constructUrl(subpath, params);
        return Axios.get(url.href, config);
    }

    async post<T>(subpath : string = "", data : any, params ?: { [key: string] : any}, config ?: AxiosRequestConfig) : Promise<PostResponse<T> | void> {
        const url = this.constructUrl(subpath, params);
        Axios.defaults.headers.post['formName'] = this.formName;
        return Axios.post<T>(url.href, data, config)
        .then(response => PostResponse.createSuccessResponse<T>(response.data))
        .catch((err : AxiosError) => this.handleAxiosError<T>(err, subpath));
    }

    async put(subpath : string = "", data : any, params ?: { [key: string] : any}) : Promise<PostResponse<unknown> | void> {
        const url = this.constructUrl(subpath, params);
        Axios.defaults.headers.post['formName'] = this.formName;
        return Axios.put(url.href, data)
        .then(() => PostResponse.createSuccessResponse(null))
        .catch((err : AxiosError) => this.handleAxiosError(err, subpath));
    }

    async delete(subpath : string = "", params ?: { [key: string] : any}) : Promise<PostResponse<unknown> | void> {
        const url = this.constructUrl(subpath, params);
        Axios.defaults.headers.post['formName'] = this.formName;
        return Axios.delete(url.href)
        .then(() => PostResponse.createSuccessResponse(null))
        .catch((err : AxiosError) => this.handleAxiosError(err, subpath));
    }

    private constructUrl(subpath ?: string, params ?: { [key: string] : any}) : URL {
        // url de desarrollo
        // const url = new URL(`http://warehousestockmanager.dev.api.prosapp.nl${this.baseUrl}${subpath ? `/${subpath}` : ''}`);
        // url produccion
        const url = new URL(`https://warehousestockmanager.pro.api.prosapp.nl${this.baseUrl}${subpath ? `/${subpath}` : ''}`);
        
        if (params) {
            for (const [param, value] of Object.entries(params)) {
                if (Array.isArray(value)) {
                    value.forEach(v => {
                        url.searchParams.append(param, v);
                    });
                } else {
                    url.searchParams.set(param, value);
                }
            }
        }
        return url;
    }

    private handleAxiosError<T>(err : AxiosError, subpath : string) : void | PostResponse<T> {
        if (err.response) {
            if (err.response.status === 400) {
                if (err.response.data.errors) {
                    return PostResponse.createErrorResponse(err.response.data.errors);
                } else if (typeof err.response.data === 'object') {
                    return PostResponse.createErrorResponse(err.response.data);
                } else {
                    return PostResponse.createErrorResponse({ "default": [err.response.data] });
                }
            } else {
                console.error(`Request to ${this.baseUrl}/${subpath} failed. Status: ${err.response.status}, message: ${err.message}`);
            }
        } else {
            console.error(`Request to ${subpath} failed: ${err}`);
        }
    }
}

export class PostResponse<T> {
    isSuccess !: boolean;
    data !: T;
    errorState !: { [key : string] : string[]}

    static createSuccessResponse<T>(data : T) : PostResponse<T> {
        const r = new PostResponse<T>();
        r.data = data;
        r.isSuccess = true;
        return Object.freeze(r);
    }

    static createErrorResponse<T>(modelState : { [key : string] : string[]}) : PostResponse<T> {
        const r = new PostResponse<T>();
        r.errorState = modelState;
        r.isSuccess = false;
        return Object.freeze(r);
    }
}
